import {
  type CanvasElementFrom,
  type PropsOf,
  type StyleConfigProps,
  type TitlePropsModel,
  type VisibilityElementProps,
} from '@blaze-runtime/engine-domain';
import {
  type BorderRadiusValue,
  type CanvasElementPropertiesWithData,
  type ElementTitleStyleProps,
  type PredefinedRowAction,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputContainerItemActionModel,
  type PropertiesConfigInputVisualEffectModel,
} from '@blaze-shared/elements-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

export type ChatElement = CanvasElementFrom<'chat'>;

export type ChatMessageAlignment = 'left' | 'split';

type PropsModel = CanvasElementPropertiesWithData &
  StyleConfigProps &
  VisibilityElementProps &
  TitlePropsModel;

type ChatFontStyleProps = {
  fontFamily?: string;
  fontWeight?: string;
  fontSize?: ValueWithUnitOfMeasure;
  lineHeight?: ValueWithUnitOfMeasure;
};

export type ChatPropsModel = PropsModel & {
  /** @deprecated */
  version: number;
  content: {
    input: {
      messages: {
        id?: string;
        messageBody?: string;
        createdTime?: string;
        updatedTime?: string;
        modifiedTime?: string;
        sender?: string;
        conversation?: string;
        attachments?: string;
      };
      conversations: {
        id?: string;
        name?: string;
        members?: string;
        createdBy?: string;
      };
      conversationsInfo: {
        conversation?: string;
        membersEmail?: string;
        membersName?: string;
        membersImage?: string;
        lastMessageId?: string;
      };
      members: {
        status?: string;
        statusDescription?: string;
      };
    };
    output: {
      actions: PropertiesConfigInputContainerItemActionModel[];
    };
    intro: {
      messages: { message: string }[];
      image?: string;
      showImage: boolean;
    };
    messages: {
      enabled: boolean;
    };
    conversations: {
      enabled: boolean;
    };
    conversationsInfo: {
      enabled: boolean;
    };
    actions: {
      sendMessage?: PredefinedRowAction;
      addConversation?: PredefinedRowAction;
      editConversation?: PredefinedRowAction;
      deleteConversation?: PredefinedRowAction;
      leaveConversation?: PredefinedRowAction;
    };
    enableMessageEdit: boolean;
    showConversationsMenu: boolean;
    showConversationFilters: boolean;
    conversationsManagementEnabled: boolean;
    messageAlignment: ChatMessageAlignment;
  };
  style: {
    general: {
      title: PropertiesConfigInputColorModel;
      background: PropertiesConfigInputColorModel;
      outline: PropertiesConfigInputColorModel;
      accent: PropertiesConfigInputColorModel;
      accentLabel: PropertiesConfigInputColorModel;
      radius: BorderRadiusValue;
      visualEffect: PropertiesConfigInputVisualEffectModel;
    };
    titleBlock: ElementTitleStyleProps;
    participants: {
      label: PropertiesConfigInputColorModel;
      hoverBackground: PropertiesConfigInputColorModel;
      selectedBackground: PropertiesConfigInputColorModel;
    };
    messages: {
      myText: PropertiesConfigInputColorModel;
      myBackground: PropertiesConfigInputColorModel;
      othersText: PropertiesConfigInputColorModel;
      othersBackground: PropertiesConfigInputColorModel;
    } & ChatFontStyleProps;
    input: {
      text: PropertiesConfigInputColorModel;
      background: PropertiesConfigInputColorModel;
      outline: PropertiesConfigInputColorModel;
    } & ChatFontStyleProps;
  };
};

export const CHAT_DEFAULTS = {
  version: 1,
  content: {
    title: {
      enabled: true,
      label: 'Chat',
      align: 'left',
      iconColor: { color1: '$t.title', selectorType: 'solid' },
    },
    input: {
      messages: {},
      conversations: {},
      conversationsInfo: {},
      members: {},
    },
    output: {
      actions: [],
    },
    intro: {
      messages: [],
      showImage: false,
      image: undefined,
    },
    messages: {
      enabled: true,
    },
    conversations: {
      enabled: true,
    },
    conversationsInfo: {
      enabled: true,
    },
    actions: {
      sendMessage: {
        enabled: false,
      },
      addConversation: {
        enabled: false,
      },
      editConversation: {
        enabled: false,
      },
      deleteConversation: {
        enabled: false,
      },
      leaveConversation: {
        enabled: false,
      },
    },
    enableMessageEdit: false,
    showConversationsMenu: true,
    showConversationFilters: true,
    conversationsManagementEnabled: true,
    messageAlignment: 'left',
  },
  style: {
    general: {
      title: { color1: '$t.title', selectorType: 'solid' },
      background: { color1: '$t.canvas', selectorType: 'solid' },
      outline: { color1: '$t.outline', selectorType: 'solid' },
      accent: { color1: '$t.accent', selectorType: 'solid' },
      accentLabel: { color1: '$t.accentLabel', selectorType: 'solid' },
      radius: '4px',
      visualEffect: {
        type: '$t.effectType',
        data: {
          x: '$t.effectX',
          y: '$t.effectY',
          blur: '$t.effectBlur',
          color1: '$t.effectColor1',
        },
      },
    },
    titleBlock: {
      fontStyle: '$t.headerFontStyle' as any,
      fontSize: '$t.headerFontSize',
      fontFamily: '$t.headerFontFamily',
      fontWeight: '$t.headerFontWeight',
      iconSize: '$t.headerIconSize',
    },
    participants: {
      label: { color1: '$t.value', selectorType: 'solid' },
      hoverBackground: { color1: '$t.fill', selectorType: 'solid' },
      selectedBackground: { color1: '$t.accent_a05', selectorType: 'solid' },
    },
    messages: {
      myText: { color1: '$t.value', selectorType: 'solid' },
      myBackground: { color1: '$t.canvas', selectorType: 'solid' },
      othersText: { color1: '$t.value', selectorType: 'solid' },
      othersBackground: { color1: '$t.accent_a05', selectorType: 'solid' },
      fontFamily: '$t.fontFamily',
      fontWeight: '$t.fontWeight',
      fontSize: '$t.fontSize',
      lineHeight: '$t.lineHeight',
    },
    input: {
      text: { color1: '$t.value', selectorType: 'solid' },
      background: { color1: '$t.canvas', selectorType: 'none' },
      outline: { color1: '$t.outline', selectorType: 'none' },
      fontFamily: '$t.fontFamily',
      fontWeight: '$t.fontWeight',
      fontSize: '$t.fontSize',
      lineHeight: '$t.lineHeight',
    },
  },
} satisfies PropsOf<'chat'>;
